import { Box, Divider, Stack } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useFormContext, Controller } from "react-hook-form";
import ByzMuiSelect from "src/components/core-components/mui/ByzMuiSelect";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import { useTranslate } from "src/locales";
import { FORM_STYLES } from "../constants";
import { PageOneFormData } from "../models";

interface AssessmentFieldProps {
  labelKey: string;
  selectName: string;
  descriptionName: string;
  isTextAreaOnly?: boolean;
}

const AssessmentField = ({ labelKey, selectName, descriptionName, isTextAreaOnly }: AssessmentFieldProps) => {
  const { t } = useTranslate();
  const { control } = useFormContext();

  return (
    <Grid md={3} xs={12} sx={{ padding: "0px" }}>
      <Grid sx={{ paddingTop: "10px" }}>
        <ByzMuiTypography fontWeight={400} marginLeft="0" textAlign="left" sx={{ width: "90%" }}>
          {t(labelKey)}
        </ByzMuiTypography>
      </Grid>
      {!isTextAreaOnly && (
        <Grid sx={{ paddingTop: "10px" }}>
          <Controller
            name={selectName}
            control={control}
            render={({ field }) => (
              <ByzMuiSelect
                {...field}
                renderInput={(params) => <ByzMuiTextField {...params} />}
                options={[]}
                label={t("open-process-menu.select", "capitalize")}
                placeholder={t("open-process-menu.select", "capitalize")}
                sx={{ maxWidth: "300px", width: "100%" }}
              />
            )}
          />
        </Grid>
      )}
      <Grid sx={{ paddingTop: "10px" }}>
        {!isTextAreaOnly && (
          <ByzMuiTypography fontWeight={400} marginLeft="0" textAlign="left">
            {t("adult-patient-assessment.description")}
          </ByzMuiTypography>
        )}
        <Grid sx={{ paddingTop: "10px" }}>
          <Controller
            name={descriptionName}
            control={control}
            render={({ field }) => (
              <ByzMuiTextField
                {...field}
                multiline
                rows={2}
                sx={{ width: "100%", maxWidth: "300px" }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export const PatientAssessmentDetails = () => {
  const { t } = useTranslate();
  const { control } = useFormContext<PageOneFormData>();

  return (
    <Grid md={12} xs={12} container direction={"row"} spacing={2} sx={{ padding: "0px", paddingTop: "20px", margin: "0px" }}>
      <Box sx={{ border: FORM_STYLES.sectionBorder, borderRadius: FORM_STYLES.sectionBorderRadius, padding: FORM_STYLES.sectionPadding, width: "100%" }}>
        <Grid sx={{ backgroundColor: FORM_STYLES.titleBackgroundColor, color: FORM_STYLES.titleColor, borderRadius: FORM_STYLES.titleBorderRadius }}>
          <ByzMuiTypography isTitle paddingRight={"20px"} paddingTop={"5px"}>
            {t("adult-patient-assessment.patient-info")}
          </ByzMuiTypography>
        </Grid>

        {/* Row 1 */}
        <Stack direction={"row"} sx={{ paddingTop: "10px" }} flexWrap="wrap">
          <AssessmentField labelKey="adult-patient-assessment.admission-type" selectName="patientAssessment.admissionType" descriptionName="patientAssessment.admissionDescription" />
          <AssessmentField labelKey="adult-patient-assessment.consciousness-status" selectName="patientAssessment.consciousnessStatus" descriptionName="patientAssessment.consciousnessDescription" />
          <AssessmentField labelKey="adult-patient-assessment.sleep-pattern" selectName="patientAssessment.sleepPattern" descriptionName="patientAssessment.sleepDescription" />
          <AssessmentField labelKey="adult-patient-assessment.defecation" selectName="patientAssessment.defecation" descriptionName="patientAssessment.defecationDescription" />
          <AssessmentField labelKey="adult-patient-assessment.habits" selectName="patientAssessment.habits" descriptionName="patientAssessment.habitsDescription" />
        </Stack>

        <Divider sx={{ borderColor: FORM_STYLES.dividerColor, borderBottomWidth: FORM_STYLES.dividerWidth, my: 2 }} />

        {/* Row 2 */}
        <Stack direction={"row"} sx={{ paddingTop: "10px" }} flexWrap="wrap">
          <AssessmentField labelKey="adult-patient-assessment.referring-department" selectName="patientAssessment.referringDepartment" descriptionName="patientAssessment.referringDepartmentDescription" />
          <AssessmentField labelKey="adult-patient-assessment.interpreter-requirement" selectName="patientAssessment.interpreterRequirement" descriptionName="patientAssessment.interpreterDescription" />
          <AssessmentField labelKey="adult-patient-assessment.hearing-speech" selectName="patientAssessment.hearingSpeech" descriptionName="patientAssessment.hearingSpeechDescription" />
          <AssessmentField labelKey="adult-patient-assessment.vision" selectName="patientAssessment.vision" descriptionName="patientAssessment.visionDescription" />
          <AssessmentField labelKey="adult-patient-assessment.prostheses" selectName="patientAssessment.prostheses" descriptionName="patientAssessment.prosthesesDescription" />
        </Stack>

        <Divider sx={{ borderColor: FORM_STYLES.dividerColor, borderBottomWidth: FORM_STYLES.dividerWidth, my: 2 }} />

        {/* Row 3 */}
        <Stack direction={"row"} sx={{ paddingTop: "10px" }} flexWrap="wrap">
          <AssessmentField labelKey="adult-patient-assessment.communication-status" selectName="patientAssessment.communicationStatus" descriptionName="patientAssessment.communicationDescription" />
          <AssessmentField labelKey="adult-patient-assessment.psychological" selectName="patientAssessment.psychological" descriptionName="patientAssessment.psychologicalDescription" />
          <AssessmentField labelKey="adult-patient-assessment.musculoskeletal-system" selectName="patientAssessment.musculoskeletalSystem" descriptionName="patientAssessment.musculoskeletalDescription" />
          <AssessmentField labelKey="adult-patient-assessment.peripheral-vascular-system" selectName="patientAssessment.peripheralVascularSystem" descriptionName="patientAssessment.peripheralVascularDescription" />
          <AssessmentField labelKey="adult-patient-assessment.mobility" selectName="patientAssessment.mobility" descriptionName="patientAssessment.mobilityDescription" />
        </Stack>

        <Divider sx={{ borderColor: FORM_STYLES.dividerColor, borderBottomWidth: FORM_STYLES.dividerWidth, my: 2 }} />

        {/* Row 4 */}
        <Stack direction={"row"} sx={{ paddingTop: "10px" }} flexWrap="wrap">
          <AssessmentField labelKey="adult-patient-assessment.mouth-tongue" selectName="patientAssessment.mouthTongue" descriptionName="patientAssessment.mouthTongueDescription" />
          <AssessmentField labelKey="adult-patient-assessment.respiration" selectName="patientAssessment.respiration" descriptionName="patientAssessment.respirationDescription" />
          <AssessmentField labelKey="adult-patient-assessment.urinary" selectName="patientAssessment.urinary" descriptionName="patientAssessment.urinaryDescription" />
          <AssessmentField labelKey="adult-patient-assessment.gastrointestinal" selectName="patientAssessment.gastrointestinal" descriptionName="patientAssessment.gastrointestinalDescription" />
          <AssessmentField labelKey="adult-patient-assessment.nails" selectName="patientAssessment.nails" descriptionName="patientAssessment.nailsDescription" />
        </Stack>

        <Divider sx={{ borderColor: FORM_STYLES.dividerColor, borderBottomWidth: FORM_STYLES.dividerWidth, my: 2 }} />

        {/* Row 5 */}
        <Stack direction={"row"} sx={{ paddingTop: "10px" }} flexWrap="wrap">
          <AssessmentField labelKey="adult-patient-assessment.hair" selectName="patientAssessment.hair" descriptionName="patientAssessment.hairDescription" />
          <AssessmentField labelKey="adult-patient-assessment.bleeding" selectName="patientAssessment.bleeding" descriptionName="patientAssessment.bleedingDescription" />
          <AssessmentField labelKey="adult-patient-assessment.cardiovascular" selectName="patientAssessment.cardiovascular" descriptionName="patientAssessment.cardiovascularDescription" />
          <AssessmentField labelKey="adult-patient-assessment.skin" selectName="patientAssessment.skin" descriptionName="patientAssessment.skinDescription" />
          <AssessmentField labelKey="adult-patient-assessment.environment-orientation" selectName="patientAssessment.environmentOrientation" descriptionName="patientAssessment.environmentOrientationDescription" />
        </Stack>

        <Divider sx={{ borderColor: FORM_STYLES.dividerColor, borderBottomWidth: FORM_STYLES.dividerWidth, my: 2 }} />

        {/* Row 6: habits */}
        <Stack direction={"row"} sx={{ paddingTop: "10px" }} flexWrap="wrap">
          <AssessmentField labelKey="adult-patient-assessment.reading-habit" selectName="" descriptionName="habits.reading" isTextAreaOnly />
          <AssessmentField labelKey="adult-patient-assessment.radio-tv" selectName="" descriptionName="habits.radioTv" isTextAreaOnly />
          <AssessmentField labelKey="adult-patient-assessment.other-habits" selectName="" descriptionName="habits.other" isTextAreaOnly />
        </Stack>

        {/* Row 7: Physical Findings */}
        <Stack direction={"row"} sx={{ paddingTop: "10px" }} flexWrap="wrap">
          {([
            { key: "Edema", labelKey: "edema", descKey: "edemaDescription" },
            { key: "Wound", labelKey: "wound", descKey: "woundDescription" },
            { key: "PressureInjury", labelKey: "pressure-injury", descKey: "pressureInjuryDescription" },
          ] as const).map((findings) => (
             <Grid key={findings.key} md={3} xs={12} sx={{ padding: "0px" }}>
              <Grid sx={{ paddingTop: "10px" }}>
                <Stack direction={"row"} sx={{ paddingTop: "10px" }}>
                  <Controller
                    name={`physicalFindings.has${findings.key}`}
                    control={control}
                    render={({ field }) => (
                      <ByzMuiCheckBox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                  <ByzMuiTypography fontWeight={"400"} sx={{ display: "flex", alignItems: "center", height: "44px" }}>
                    {t(`adult-patient-assessment.${findings.labelKey}`)}
                  </ByzMuiTypography>
                </Stack>
                <Grid sx={{ paddingTop: "10px" }}>
                  <Controller
                    name={`physicalFindings.${findings.descKey}`}
                    control={control}
                    render={({ field }) => (
                      <ByzMuiTextField {...field} multiline rows={2} sx={{ width: "100%", maxWidth: "300px" }} />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Stack>
      </Box>
    </Grid>
  );
};
